import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BasketSupplierStore } from '@lib/basket/suppliers-basket/basket-supplier/data-access';
import { Observable } from 'rxjs';
import {
    BasketComment,
    BasketItem,
    BasketItemRequest,
    CompanySetting,
    DeleteAttachmentFileRequest,
    SupplierBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { DeliveryCondition } from '@lib/delivery-conditions';
import { PaymentTerm } from '@lib/payment-terms';
import { FrameContractSelection } from '@lib/frame-contract-selection';
import { IBudgetExceedance, IPurchasingGroupResolution } from '@lib/shared/basket-service';

@Component({
    selector: 'basket-supplier-feature',
    templateUrl: './basket-supplier-feature.component.html',
    styleUrl: './basket-supplier-feature.component.scss',
    providers: [BasketSupplierStore],
    standalone: false
})
export class BasketSupplierFeatureComponent implements OnInit, OnChanges {
    basketSupplierStore = inject(BasketSupplierStore);

    @Input() basketSupplierId: string;
    @Input() isEditableStep!: boolean;
    @Input() basketToggleStatus!: boolean;
    @Input() companySetting!: CompanySetting;
    @Input() budgetOverruns: IBudgetExceedance[] | null = [];
    @Input() resolvedPurchasingGroups: readonly IPurchasingGroupResolution[] = [];
    @Input() purchasingGroupVisible = true;
    @Input() companyCodeId: string | null = null;

    @Output() productNumberValidationMessageShown = new EventEmitter<boolean>();
    @Output() desiredDeliveryDateValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() basketItemDeleted = new EventEmitter<BasketItemRequest>();

    @Output() accountingValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() classificationValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() materialGroupValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() siteValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() paymentTermsValidated = new EventEmitter<{ valid: boolean; basketSupplierId: string }>();
    @Output() frameContractValidated = new EventEmitter<{ valid: boolean; basketSupplierId: string }>();

    supplierBasket$!: Observable<SupplierBasket>;
    basketItems$!: Observable<BasketItem[]>;
    deliveryCondition$!: Observable<DeliveryCondition>;
    paymentTerms$!: Observable<PaymentTerm[]>;
    isPaymentTermRequired$!: Observable<boolean>;
    frameContracts$!: Observable<FrameContractSelection[]>;
    isGeneralAgreementRequired$!: Observable<boolean>;

    ngOnInit() {
        this.basketSupplierStore.getMaxUploadSize$();
        this.streamSupplierBasket();
        this.streamBasketItems();
        this.streamDeliveryCondition();
        this.streamPaymentTerms();
        this.streamIsPaymentTermRequired();
        this.streamFrameContracts();
        this.streamIsGeneralAgreementRequired();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes.basketSupplierId || changes.companyCodeId) && this.basketSupplierId) {
            this.basketSupplierStore.loadBasketSupplierWithItems$(this.basketSupplierId);
        }
    }

    saveDeliveryCondition(newCondition: DeliveryCondition): void {
        this.basketSupplierStore.saveDeliveryCondition$(newCondition);
    }

    onDeleteDeliveryCondition(): void {
        this.basketSupplierStore.deleteDeliveryCondition$();
    }

    deleteBasketItem(item: BasketItemRequest) {
        this.basketSupplierStore.deleteBasketItem$(item);
        this.basketItemDeleted.emit(item);
    }

    openAttachmentDialog(supplierBasketId: string) {
        this.basketSupplierStore.openUploadFileDialog$(supplierBasketId);
    }

    saveSupplierComment(comment: BasketComment): void {
        this.basketSupplierStore.updateSupplierComments$(comment);
    }

    deleteAttachmentFile(attachmentFile: DeleteAttachmentFileRequest) {
        this.basketSupplierStore.deleteAttachmentFile$(attachmentFile);
    }

    savePersonComment(comment: BasketComment): void {
        this.basketSupplierStore.updatePersonComments$(comment);
    }

    onShowProductNumberValidationMessage(isShowMessage: boolean) {
        this.productNumberValidationMessageShown.emit(isShowMessage);
    }

    onDesiredDeliveryDateValidated(event: { valid: boolean; basketItemId: number }) {
        this.desiredDeliveryDateValidated.emit(event);
    }

    onAccountingValidated(event: { valid: boolean; basketItemId: number }) {
        this.accountingValidated.emit(event);
    }

    onSiteValidated(event: { valid: boolean; basketItemId: number }) {
        this.siteValidated.emit(event);
    }

    onClassificationValidated(event: { valid: boolean; basketItemId: number }) {
        this.classificationValidated.emit(event);
    }

    onMaterialGroupValidated(event: { valid: boolean; basketItemId: number }) {
        this.materialGroupValidated.emit(event);
    }

    onPaymentTermSelectionOpen() {
        this.basketSupplierStore.openPaymentTermDialog$();
    }

    deleteCurrentPaymentTerm(): void {
        this.basketSupplierStore.deletePaymentTerm$();
    }

    onPaymentTermValidated(valid: boolean): void {
        const paymentTerm = { valid: valid, basketSupplierId: this.basketSupplierId };
        this.paymentTermsValidated.emit(paymentTerm);
    }

    onFrameContractSelectionChanged(frameContractId: number | null) {
        if (frameContractId == null) {
            this.basketSupplierStore.deleteFrameContractSelection$();
            return;
        }
        const supplierBasketId = this.basketSupplierId;
        if (!supplierBasketId) {
            return;
        }
        this.basketSupplierStore.updateFrameContractSelection$({ supplierBasketId, frameContractId });
    }

    onFrameContractValidated(valid: boolean) {
        this.frameContractValidated.emit({ valid, basketSupplierId: this.basketSupplierId });
    }

    private streamFrameContracts(): void {
        this.frameContracts$ = this.basketSupplierStore.frameContracts;
    }

    private streamIsGeneralAgreementRequired(): void {
        this.isGeneralAgreementRequired$ = this.basketSupplierStore.isGeneralAgreementRequired;
    }

    private streamPaymentTerms(): void {
        this.paymentTerms$ = this.basketSupplierStore.paymentTerms;
    }

    private streamSupplierBasket(): void {
        this.supplierBasket$ = this.basketSupplierStore.supplierBasket;
    }

    private streamBasketItems(): void {
        this.basketItems$ = this.basketSupplierStore.basketItems;
    }

    private streamDeliveryCondition(): void {
        this.deliveryCondition$ = this.basketSupplierStore.deliveryCondition;
    }
    private streamIsPaymentTermRequired(): void {
        this.isPaymentTermRequired$ = this.basketSupplierStore.isPaymentTermRequired;
    }
}
